import { useEffect, useRef, useCallback, useState } from "react";
import { Toast } from "./Toast";
import moment from "moment";

const INACTIVITY_LIMIT = 15 * 60 * 1000; // 30 minute
const LOGOUT_TIME_LIMIT = 10; // in minutes
const CHECK_INTERVAL = 1000; // 1 second
const WARNING_TIME = 10 * 1000; // Warning at 10s

const AppLogout = ({ children }) => {
  const [token, setToken] = useState(
    localStorage.getItem("token") || sessionStorage.getItem("token"),
  );
  const lastActivityRef = useRef(moment());
  const isLoggingOutRef = useRef(false);
  const [remainingTime, setRemainingTime] = useState(INACTIVITY_LIMIT);
  const [showWarning, setShowWarning] = useState(false);

  // Check if user is on auth pages
  const isAuthPage = useCallback(() => {
    const hash = window.location.hash.toLowerCase();
    const path = hash.replace("#/", "").split("?")[0];
    const authPaths = ["", "login", "register", "forgot", "create-password"];
    return authPaths.includes(path);
  }, []);

  // Logout action
  const logoutAction = useCallback(
    (reason = "Please re-login to access") => {
      console.log(`[AppLogout] logoutAction called with reason: "${reason}"`);
      console.log(`[AppLogout] State: isLoggingOut=${isLoggingOutRef.current}, isAuthPage=${isAuthPage()}, hasToken=${!!token}`);

      if (isLoggingOutRef.current || isAuthPage()) {
        console.log(`[AppLogout] Logout aborted: already logging out or on auth page`);
        return;
      }

      isLoggingOutRef.current = true;
      setShowWarning(false);
      Toast(reason);

      console.log(`[AppLogout] Executing logout: clearing storage and redirecting...`);
      sessionStorage.clear();
      localStorage.removeItem("token");
      localStorage.removeItem("last_active");
      localStorage.removeItem("login_time");
      setToken(null);

      console.log(`[AppLogout] Storage cleared. Redirecting to /#/ and reloading.`);
      window.location.href = "/#/";
      setTimeout(() => {
        console.log(`[AppLogout] Performing final page reload`);
        window.location.reload();
      }, 100);
    },
    [isAuthPage],
  );

  // Reset activity
  const resetActivity = useCallback(() => {
    if (isLoggingOutRef.current || isAuthPage() || !token) return;

    const now = moment();
    lastActivityRef.current = now;
    localStorage.setItem("last_active", now.toString());
    
    // Log occasionally or when warning is shown to avoid flooding
    if (showWarning) {
      console.log(`[AppLogout] Activity reset. Warning cleared. New last_active: ${now.format('HH:mm:ss')}`);
      setShowWarning(false);
    }
  }, [isAuthPage, showWarning, token]);

  // 1. Token Switch Listener & Initial Reset
  useEffect(() => {
    const checkToken = () => {
      const currentToken =
        localStorage.getItem("token") || sessionStorage.getItem("token");
      if (currentToken !== token) {
        setToken(currentToken);
      }
    };

    if (token && !isAuthPage()) {
      const now = moment();
      localStorage.setItem("last_active", now.toString());
      lastActivityRef.current = now;
      setRemainingTime(INACTIVITY_LIMIT);
      // // console.log("� Session Active: Timer started at 60s");
    }

    const interval = setInterval(checkToken, 2000); // Check for token changes every 2s
    return () => clearInterval(interval);
  }, [token, isAuthPage]);

  // 2. Activity Monitoring
  useEffect(() => {
    if (!token || isAuthPage() || isLoggingOutRef.current) return;

    const events = [
      "mousemove",
      "mousedown",
      "click",
      "keypress",
      "keydown",
      "keyup",
      "scroll",
      "touchstart",
      "wheel",
    ];
    const handleEvent = () => resetActivity();

    events.forEach((event) =>
      window.addEventListener(event, handleEvent, { passive: true }),
    );
    return () =>
      events.forEach((event) => window.removeEventListener(event, handleEvent));
  }, [token, isAuthPage, resetActivity]);

  // 3. Multi-tab Sync
  useEffect(() => {
    const handleStorageChange = (e) => {
      console.log(`[AppLogout] Storage event: key=${e.key}, newValue=${!!e.newValue}`);
      if (e.key === "last_active" && e.newValue) {
        console.log(`[AppLogout] last_active updated from another tab: ${e.newValue}`);
      }
      if (e.key === "token") {
        setToken(e.newValue);
        if (!e.newValue && !isAuthPage()) {
          console.warn(`[AppLogout] Token removed in another tab. Triggering logout...`);
          window.location.href = "/#/";
        }
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [isAuthPage]);

  // 4. Main Timer Interval
  useEffect(() => {
    if (!token || isAuthPage() || isLoggingOutRef.current) {
      setRemainingTime(INACTIVITY_LIMIT);
      return;
    }

    const interval = setInterval(() => {
      // Double check token inside interval
      if (!token || isAuthPage() || isLoggingOutRef.current) {
        console.log(`[AppLogout] Interval check aborted: token=${!!token}, isAuthPage=${isAuthPage()}, isLoggingOut=${isLoggingOutRef.current}`);
        clearInterval(interval);
        setRemainingTime(INACTIVITY_LIMIT); // Reset timer if token disappears
        return;
      }
      
      const now = moment();
      const lastActive =
        localStorage.getItem("last_active") || lastActivityRef.current;
      const logoutTime = moment(lastActive).add(LOGOUT_TIME_LIMIT, "minutes");
      const remaining = logoutTime.diff(now, "milliseconds"); // Use milliseconds for consistency

      setRemainingTime(remaining);

      // Log every 30 seconds or when close to expiry
      const remainingSec = Math.floor(remaining / 1000);
      if (remainingSec % 30 === 0 || remaining <= WARNING_TIME) {
        if (remaining > 0) {
          console.log(`[AppLogout] Timer check: Remaining=${remainingSec}s, LastActive=${moment(lastActive).format('HH:mm:ss')}`);
        }
      }

      if (remaining <= 0) {
        console.warn(`[AppLogout] Inactivity limit reached. Triggering logoutAction.`);
        logoutAction("You have been logged out due to inactivity");
      } else if (remaining <= WARNING_TIME) {
        if (!showWarning) {
           console.log(`[AppLogout] Showing inactivity warning toast. Remaining: ${remainingSec}s`);
           setShowWarning(true);
        }
      } else {
        if (showWarning) setShowWarning(false);
      }
    }, CHECK_INTERVAL);

    return () => clearInterval(interval);
  }, [token, isAuthPage, logoutAction, showWarning]);

  // 5. Warning Toast
  useEffect(() => {
    if (token && showWarning && !isAuthPage() && !isLoggingOutRef.current) {
      const seconds = Math.ceil(remainingTime / 1000);
      if ([10, 5, 2].includes(seconds)) {
        // // console.log(`Session expires in ${seconds}s`, "warning");
      }
    }
  }, [token, showWarning, remainingTime, isAuthPage]);

  return children;
};

export default AppLogout;
